import { QueryClient } from '@tanstack/react-query';

/**
 * React Query Client Configuration
 *
 * Centralized configuration for React Query with optimized defaults:
 * - Caching strategy for RUM data (30s stale time, 5min cache time)
 * - Retry logic for failed requests
 * - Background refetching on window focus
 * - Optimistic updates support
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Consider data fresh for 30 seconds
      staleTime: 30 * 1000,

      // Keep unused data in cache for 5 minutes
      cacheTime: 5 * 60 * 1000,

      // Retry failed requests up to 2 times
      retry: 2,

      // Retry delay increases exponentially (1s, 2s, 4s)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Disable refetch on window focus to prevent interrupting user interactions
      // Users can manually refresh when needed
      refetchOnWindowFocus: false,

      // Don't refetch on mount if data is still fresh
      refetchOnMount: false,

      // Refetch in background when data becomes stale
      refetchOnReconnect: true,

      // Keep previous data while fetching new data (prevents UI flicker)
      keepPreviousData: true,

      // Error handling
      onError: (error) => {
        console.error('React Query Error:', error);
        // Could integrate with error reporting service here (e.g., Sentry)
      },
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,

      onError: (error) => {
        console.error('Mutation Error:', error);
      },
    },
  },
});

// Query key factory for consistent cache keys
export const queryKeys = {
  // Session metrics queries
  sessionMetrics: (appId, timeRange) => ['sessionMetrics', appId, timeRange],

  // Web Vitals queries
  webVitals: (appId, timeRange) => ['webVitals', appId, timeRange],

  // Session search/list queries
  sessions: (params) => ['sessions', params],
  sessionDetail: (sessionId) => ['session', sessionId],
  sessionEvents: (sessionId, limit) => ['sessionEvents', sessionId, limit],

  // Error queries
  errorMetrics: (appId, timeRange) => ['errorMetrics', appId, timeRange],
  errorList: (params) => ['errors', params],

  // Performance queries
  performanceMetrics: (appId, timeRange, metric) => ['performanceMetrics', appId, timeRange, metric],
  pagePerformance: (appId, timeRange, url) => ['pagePerformance', appId, timeRange, url],

  // Analytics queries
  userJourneys: (appId, timeRange) => ['userJourneys', appId, timeRange],
  funnelAnalysis: (appId, steps, timeRange) => ['funnelAnalysis', appId, steps, timeRange],
  performanceAnalytics: (appId, timeRange) => ['performanceAnalytics', appId, timeRange],

  // Autocomplete queries
  uniqueUrls: (appId, timeRange, limit) => ['uniqueUrls', appId, timeRange, limit],
  uniqueEventNames: (appId, timeRange, limit) => ['uniqueEventNames', appId, timeRange, limit],

  // App management
  rumApps: () => ['rumApps'],
};

export default queryClient;
